import { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import lgs from "../assets/LGS/banner2.jpg";
import lgs1 from "../assets/LGS/banner6.jpg";
import lgs2 from "../assets/LGS/banner7.jpg";
import lgs3 from "../assets/LGS/banner8.jpg";
import waterproofing from "../assets/Waterproofing/banner3.jpg";
import waterproofing1 from "../assets/Waterproofing/banner9.jpg";
import waterproofing2 from "../assets/Waterproofing/banner10.jpg";
import waterproofing3 from "../assets/Waterproofing/banner11.jpg";
import terrazzo from "../assets/Terrazzo/banner2.jpg";
import terrazzo1 from "../assets/Terrazzo/banner7.jpg";
import terrazzo2 from "../assets/Terrazzo/banner5.jpeg";
import terrazzo3 from "../assets/Terrazzo/banner8.jpg";
import { MdListAlt, MdWhatsapp, MdCall, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { IoMdCall } from "react-icons/io";
import { FaCheckCircle } from "react-icons/fa";

const solutions = [
  {
    title: "Light Gauge Steel (LGS)",
    slug: "light-gauge-steel",
    insight:
      "Light Gauge Steel delivers a lightweight yet high-strength framework that keeps builds on schedule, cuts waste, and stays resilient against pests, moisture and fire while remaining fully recyclable.",
    advantages: [
      "High strength-to-weight ratio",
      "Faster builds with predictable timelines",
      "Precision-engineered components reducing onsite errors",
      "Adaptable to complex or customized layouts",
      "Corrosion-resistant and long-lasting",
      "Low maintenance lifecycle performance",
    ],
    image: lgs,
    images: [lgs1, lgs2, lgs3], // Add multiple images for slider
  },
  {
    title: "Waterproofing Solutions",
    slug: "waterproofing-solutions",
    insight:
      "Advanced waterproofing systems engineered to prevent leakage, seepage and structural water damage. Designed for both new construction and remedial works, our solutions enhance durability, structural integrity and lifecycle performance in high-moisture and exposed environments.",
    advantages: [
      "High-performance moisture barrier systems",
      "Reliable protection in harsh weather conditions",
      "Long-term durability and lifecycle value",
      "Reduced maintenance and repair costs",
      "Suitable for new builds and refurbishment projects",
    ],
    image: waterproofing,
    images: [waterproofing1, waterproofing2, waterproofing3],
  },
  {
    title: "Terrazzo Flooring Solutions",
    slug: "terrazzo-flooring",
    insight:
      "Premium terrazzo flooring systems designed for high-traffic environments where aesthetics and durability are equally critical. Combining decorative aggregates with advanced binding systems, terrazzo delivers a seamless, polished architectural finish with exceptional wear resistance.",
    advantages: [
      "Custom color and aggregate combinations",
      "Seamless polished architectural finish",
      "High resistance to wear and heavy foot traffic",
      "Long service life with minimal maintenance",
      "Cost-effective alternative to natural stone",
    ],
    image: terrazzo,
    images: [terrazzo1, terrazzo2, terrazzo3],
  },
];

function ImageSlider({ images, alt }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef(null);

  const startAutoSlide = () => {
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, 4000);
  };

  const stopAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
  }, [currentIndex]);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  if (images.length <= 1) {
    return (
      <div className="solution-media">
        <img src={images[0]} alt={alt} />
      </div>
    );
  }

  return (
    <div className="solution-media-slider" onMouseEnter={stopAutoSlide} onMouseLeave={startAutoSlide}>
      <div className="slider-track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {images.map((img, idx) => (
          <div key={idx} className="slide-item">
            <img src={img} alt={`${alt} - ${idx + 1}`} />
          </div>
        ))}
      </div>
      
      <button className="slider-nav slider-prev" onClick={prevSlide} aria-label="Previous slide">
        <MdChevronLeft size={24} />
      </button>
      <button className="slider-nav slider-next" onClick={nextSlide} aria-label="Next slide">
        <MdChevronRight size={24} />
      </button>
      
      <div className="slider-dots">
        {images.map((_, idx) => (
          <button
            key={idx}
            className={`slider-dot ${idx === currentIndex ? "active" : ""}`}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
      
      <div className="slide-counter">
        {String(currentIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
      </div>
    </div>
  );
}

export default function ProductsContent() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <section className="products-section">
      <div className="products-inner">
        <header className="products-hero" data-aos="fade-up" data-aos-duration="1000">
          <span className="products-badge">
            <span className="badge-dot"></span>
            Explore Our Solutions
          </span>
          <h2 className="products-title">
            Premium Construction <span className="text-gradient">Systems & Solutions</span>
          </h2>
          <p className="products-subtext">
            Engineered for excellence, delivered with precision. Discover our comprehensive range of 
            construction solutions designed to elevate your projects.
          </p>
        </header>

        <div className="solutions-grid">
          {solutions.map((solution, index) => (
            <article
              key={solution.title}
              className="solution-card"
              data-aos="fade-up"
              data-aos-duration="800"
              data-aos-delay={index * 150}
            >
              <ImageSlider images={solution.images} alt={solution.title} />
              
              <div className="solution-body">
                <div className="solution-header">
                  <span className="solution-number">{String(index + 1).padStart(2, "0")}</span>
                  <h3 className="solution-title">{solution.title}</h3>
                </div>
                
                <p className="solution-insight">{solution.insight}</p>
                
                <div className="solution-advantages">
                  <div className="advantages-header">
                    <FaCheckCircle className="advantages-icon" />
                    <span className="advantages-label">Key Advantages</span>
                  </div>
                  <ul className="advantages-list">
                    {solution.advantages.map((advantage, idx) => (
                      <li key={idx} className="advantage-item">
                        <span className="bullet-fancy"></span>
                        <span className="advantage-text">{advantage}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="solution-actions">
                  <button 
                    className="btn-solution btn-solution-primary" 
                    onClick={() => appContext.navTo({item:'products', subItem: solution.slug})}
                  >
                    View Full Details
                    <MdChevronRight size={18} />
                  </button>
                  <button 
                    className="btn-solution btn-solution-outline" 
                    onClick={() => appContext.navTo({item:'contact_us'})}
                  >
                    <IoMdCall size={18} />
                    Contact Us
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="products-cta" data-aos="fade-up" data-aos-duration="800" data-aos-delay="200">
          <div className="cta-content">
            <h3 className="cta-title">Ready to Start Your Project?</h3>
            <p className="cta-text">
              Our team of experts is ready to help you find the perfect solution for your construction needs.
            </p>
          </div>
          <div className="cta-actions">
            <button 
              className="btn-cta btn-cta-primary"
              onClick={() => appContext.navTo({item:'request_quote'})}
            >
              <MdWhatsapp size={20} />
              Get a Quote
            </button>
            <button 
              className="btn-cta btn-cta-outline"
              onClick={() => appContext.navTo({item:'contact_us'})}
            >
              <MdCall size={20} />
              Schedule Call
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .products-section {
          padding: 80px 0 100px;
          background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 50%, #f0f9ff 100%);
          font-family: "Inter", "Segoe UI", sans-serif;
          position: relative;
          overflow: hidden;
        }

        .products-section::before {
          content: "";
          position: absolute;
          top: -50%;
          right: -20%;
          width: 60%;
          height: 150%;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.08) 0%, transparent 70%);
          pointer-events: none;
        }

        .products-section::after {
          content: "";
          position: absolute;
          bottom: -30%;
          left: -10%;
          width: 50%;
          height: 100%;
          background: radial-gradient(circle, rgba(14, 165, 233, 0.06) 0%, transparent 70%);
          pointer-events: none;
        }

        .products-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          flex-direction: column;
          gap: 60px;
          position: relative;
          z-index: 1;
        }

        /* Hero Section */
        .products-hero {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .products-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%);
          border: 1px solid rgba(56, 189, 248, 0.2);
          border-radius: 999px;
          font-size: 13px;
          font-weight: 600;
          color: #0284c7;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }

        .products-title {
          margin: 0 0 16px;
          color: #0f172a;
          font-size: 42px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }

        .text-gradient {
          background: linear-gradient(135deg, #0284c7 0%, #06b6d4 50%, #38bdf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .products-subtext {
          margin: 0;
          color: #64748b;
          line-height: 1.8;
          font-size: 17px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Solutions Grid */
        .solutions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: 32px;
        }

        /* Solution Card */
        .solution-card {
          border-radius: 28px;
          border: 1px solid rgba(148, 163, 184, 0.15);
          background: #ffffff;
          box-shadow: 
            0 4px 6px -1px rgba(0, 0, 0, 0.05),
            0 20px 40px -10px rgba(14, 165, 233, 0.1);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }

        .solution-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 28px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.3), rgba(14, 165, 233, 0.1), transparent);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }

        .solution-card:hover {
          transform: translateY(-8px);
          box-shadow: 
            0 10px 15px -3px rgba(0, 0, 0, 0.08),
            0 40px 60px -15px rgba(14, 165, 233, 0.2);
        }

        /* Image Slider */
        .solution-media-slider {
          position: relative;
          overflow: hidden;
          height: 240px;
          background: #e0f2fe;
        }

        .slider-track {
          display: flex;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          height: 100%;
        }

        .slide-item {
          min-width: 100%;
          height: 100%;
        }

        .slide-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .slider-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.9);
          color: #0284c7;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 10;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .slider-nav:hover {
          background: #0284c7;
          color: #ffffff;
          transform: translateY(-50%) scale(1.1);
        }

        .slider-prev { left: 12px; }
        .slider-next { right: 12px; }

        .slider-dots {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 10;
        }

        .slider-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 2px solid rgba(255, 255, 255, 0.6);
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
        }

        .slider-dot.active {
          background: #ffffff;
          border-color: #ffffff;
          transform: scale(1.3);
        }

        .slider-dot:hover {
          border-color: #ffffff;
        }

        .slide-counter {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(0, 0, 0, 0.6);
          color: #ffffff;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          z-index: 10;
          display:none;
        }

        /* Solution Body */
        .solution-body {
          padding: 28px;
          display: flex;
          flex-direction: column;
          justify-content: space-evenly;
          gap: 18px;
          flex: 1;
        }

        .solution-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 4px;
        }

        .solution-number {
          font-size: 14px;
          font-weight: 700;
          color: #0284c7;
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.15) 0%, rgba(14, 165, 233, 0.15) 100%);
          padding: 4px 12px;
          border-radius: 999px;
          font-family: "Courier New", monospace;
        }

        .solution-title {
          margin: 0;
          color: #0f172a;
          font-size: 20px;
          font-weight: 700;
          line-height: 1.3;
        }

        .solution-insight {
          margin: 0;
          color: #475569;
          line-height: 1.7;
          font-size: 15px;
        }

        /* Fancy Bullet Points */
        .solution-advantages {
          background: linear-gradient(135deg, rgba(224, 242, 254, 0.5) 0%, rgba(207, 250, 254, 0.3) 100%);
          border-radius: 16px;
          padding: 20px;
          border: 1px solid rgba(56, 189, 248, 0.15);
        }

        .advantages-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }

        .advantages-icon {
          color: #06b6d4;
          font-size: 18px;
        }

        .advantages-label {
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          color: #0369a1;
          letter-spacing: 0.1em;
        }

        .advantages-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .advantage-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          font-size: 14px;
          color: #334155;
          line-height: 1.6;
        }

        .bullet-fancy {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          margin-top: 1px;
          background: linear-gradient(135deg, #38bdf8 0%, #06b6d4 100%);
          -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E") no-repeat center/contain;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E") no-repeat center/contain;
          -webkit-mask-size: 65%;
          mask-size: 65%;
          position: relative;
        }

        .bullet-fancy::before {
          content: "";
          position: absolute;
          inset: -3px;
          background: linear-gradient(135deg, rgba(56, 189, 248, 0.3) 0%, rgba(6, 182, 212, 0.3) 100%);
          border-radius: 50%;
          z-index: -1;
        }

        .advantage-text {
          flex: 1;
        }

        /* Solution Actions */
        .solution-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 8px;
          padding-top: 18px;
          border-top: 1px solid rgba(148, 163, 184, 0.15);
        }

        .btn-solution {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: none;
          text-decoration: none;
        }

        .btn-solution-primary {
          background: linear-gradient(135deg, #0284c7 0%, #06b6d4 100%);
          color: #ffffff;
          box-shadow: 0 4px 14px rgba(2, 132, 199, 0.35);
        }

        .btn-solution-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(2, 132, 199, 0.45);
        }

        .btn-solution-outline {
          background: transparent;
          color: #0284c7;
          border: 2px solid rgba(2, 132, 199, 0.3);
        }

        .btn-solution-outline:hover {
          background: rgba(2, 132, 199, 0.08);
          border-color: #0284c7;
          transform: translateY(-2px);
        }

        /* CTA Section */
        .products-cta {
          border-radius: 24px;
          background: linear-gradient(135deg, rgba(2, 132, 199, 0.05) 0%, rgba(6, 182, 212, 0.08) 100%);
          padding: 40px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          border: 1px solid rgba(56, 189, 248, 0.2);
          box-shadow: 0 20px 40px rgba(14, 165, 233, 0.08);
        }

        .cta-content {
          flex: 1;
          min-width: 280px;
        }

        .cta-title {
          margin: 0 0 8px;
          color: #0f172a;
          font-size: 24px;
          font-weight: 700;
        }

        .cta-text {
          margin: 0;
          color: #64748b;
          font-size: 15px;
          line-height: 1.6;
        }

        .cta-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .btn-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .btn-cta-primary {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #ffffff;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.3);
        }

        .btn-cta-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(15, 23, 42, 0.4);
        }

        .btn-cta-outline {
          background: #ffffff;
          color: #0f172a;
          border: 2px solid rgba(15, 23, 42, 0.15);
        }

        .btn-cta-outline:hover {
          border-color: #0f172a;
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .products-section {
            padding: 50px 0 70px;
          }

          .products-inner {
            padding: 0 16px;
            gap: 40px;
          }

          .products-title {
            font-size: 32px;
          }

          .solutions-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .solution-media-slider {
            height: 200px;
          }

          .solution-body {
            padding: 22px;
          }

          .products-cta {
            padding: 28px;
            flex-direction: column;
            text-align: center;
          }

          .cta-actions {
            justify-content: center;
          }

          .bullet-fancy {
            width: 16px;
            height: 16px;
          }
        }

        @media (max-width: 480px) {
          .products-title {
            font-size: 26px;
          }

          .solution-actions {
            flex-direction: column;
          }

          .btn-solution {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
}